import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-code-viewer',
  standalone: true,
  templateUrl: './code-viewer.component.html',
  styleUrls: ['./code-viewer.component.scss'],
})
export class CodeViewerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() content: unknown = '';
  @Input() language = 'json';
  @Input() readOnly = true;

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<HTMLDivElement>;

  loading = true;
  loadFailed = false;

  private static monacoLoader: Promise<any> | null = null;
  private editor: any;
  private resizeObserver?: ResizeObserver;
  private themeSubscription?: Subscription;

  constructor(private themeService: ThemeService) {}

  ngAfterViewInit(): void {
    this.initializeEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.editor && (changes['content'] || changes['language'])) {
      const model = this.editor.getModel();
      model?.setValue(this.displayContent);
      if (model && changes['language']) {
        this.loadMonaco().then(monaco => monaco.editor.setModelLanguage(model, this.language));
      }
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.themeSubscription?.unsubscribe();
    this.editor?.dispose();
  }

  private async initializeEditor(): Promise<void> {
    try {
      const monaco = await this.loadMonaco();
      this.editor = monaco.editor.create(this.editorContainer.nativeElement, {
        value: this.displayContent,
        language: this.language,
        readOnly: this.readOnly,
        automaticLayout: false,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        folding: true,
        lineNumbers: 'on',
        renderValidationDecorations: 'on',
        fontSize: 14,
        tabSize: 2,
        padding: { top: 16, bottom: 16 },
        theme: this.monacoTheme,
      });

      this.resizeObserver = new ResizeObserver(() => this.editor?.layout());
      this.resizeObserver.observe(this.editorContainer.nativeElement);
      this.themeSubscription = this.themeService.theme$.subscribe(() => {
        monaco.editor.setTheme(this.monacoTheme);
      });
      this.loading = false;
    } catch {
      this.loading = false;
      this.loadFailed = true;
    }
  }

  private get displayContent(): string {
    if (typeof this.content !== 'string') {
      return JSON.stringify(this.content ?? {}, null, 2);
    }

    if (this.language !== 'json') return this.content;

    try {
      return JSON.stringify(JSON.parse(this.content), null, 2);
    } catch {
      return this.content;
    }
  }

  private get monacoTheme(): string {
    return this.themeService.currentTheme === 'dark' ? 'vs-dark' : 'vs';
  }

  private loadMonaco(): Promise<any> {
    if (CodeViewerComponent.monacoLoader) return CodeViewerComponent.monacoLoader;

    CodeViewerComponent.monacoLoader = new Promise((resolve, reject) => {
      const browserWindow = window as any;
      const startMonaco = () => {
        browserWindow.require.config({ paths: { vs: 'assets/monaco/vs' } });
        browserWindow.require(['vs/editor/editor.main'], () => resolve(browserWindow.monaco), reject);
      };

      if (browserWindow.monaco) {
        resolve(browserWindow.monaco);
        return;
      }
      if (browserWindow.require?.config) {
        startMonaco();
        return;
      }

      const script = document.createElement('script');
      script.src = 'assets/monaco/vs/loader.js';
      script.async = true;
      script.onload = startMonaco;
      script.onerror = () => reject(new Error('Monaco loader failed'));
      document.head.appendChild(script);
    });

    return CodeViewerComponent.monacoLoader;
  }
}
